import React, { useState } from "react";
import Carousel from "react-bootstrap/Carousel";

export type ArtworkImageCarouselProps = {
    images: string[];
    alt: string;
    imageClassName?: string;
    carouselClassName?: string;
    /** When provided, each slide renders this instead of a plain img (e.g. ZoomViewer) */
    renderSlide?: (src: string, alt: string, index: number) => React.ReactNode;
    /** Min height for the slide container when using renderSlide (avoids 0 height in flex carousel). Default 300. Ignored when fillHeight is true. */
    minSlideHeight?: number;
    /** When true, slide container fills parent height (100%) and image keeps aspect ratio. Use in full-height layouts (e.g. buy modal). */
    fillHeight?: boolean;
};

export function ArtworkImageCarousel({
    images,
    alt,
    imageClassName,
    carouselClassName,
    renderSlide,
    minSlideHeight = 300,
    fillHeight = false,
}: ArtworkImageCarouselProps) {
    const [index, setIndex] = useState(0);

    if (!images || images.length === 0) return null;

    const slideWrapperStyle: React.CSSProperties = renderSlide
        ? fillHeight
            ? { height: "100%", minHeight: 0, width: "100%" }
            : { height: minSlideHeight, minHeight: minSlideHeight, width: "100%" }
        : undefined;

    if (images.length === 1) {
        if (renderSlide) {
            return (
                <div style={slideWrapperStyle}>
                    {renderSlide(images[0], alt, 0)}
                </div>
            );
        }
        return <img src={images[0]} alt={alt} className={imageClassName} />;
    }

    return (
        <Carousel
            activeIndex={index}
            onSelect={(i) => setIndex(i ?? 0)}
            controls
            variant="light"
            interval={null}
            className={carouselClassName}
        >
            {images.map((src, i) => (
                <Carousel.Item key={i}>
                    {renderSlide ? (
                        <div style={slideWrapperStyle}>
                            {renderSlide(src, `${alt} (${i + 1}/${images.length})`, i)}
                        </div>
                    ) : (
                        <img
                            src={src}
                            alt={`${alt} (${i + 1}/${images.length})`}
                            className={imageClassName}
                        />
                    )}
                </Carousel.Item>
            ))}
        </Carousel>
    );
}

export default ArtworkImageCarousel;
